import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { formatDateGroup, formatTimeOnly } from '../utils/parsers';
import { getCategoryColorClass } from '../utils/categorization';
import { SOURCE_LOGOS, getBankLogo } from '../data/bankLogos';
import {
    isGuessTxn,
    isAmbiguousPeopleGuess,
    REVIEW_FILTER,
} from '../utils/confidence';
import RetagModal from './RetagModal';

function TransactionList({ categoryFilter = 'all' }) {
    const { filteredTransactions, retagTransaction, isSampleMode, triggerSampleLocked, setStatus } = useAppContext();
    const [expandedIdx, setExpandedIdx] = useState(null);
    const [retagIdx, setRetagIdx] = useState(null);

    const visible = filteredTransactions.filter((t) => {
        if (categoryFilter === 'all') return true;
        if (categoryFilter === REVIEW_FILTER) {
            return isGuessTxn(t) || (t.category || 'Uncategorized') === 'Uncategorized';
        }
        return (t.category || 'Uncategorized') === categoryFilter;
    });

    const grouped = {};
    visible.forEach(function (t) {
        const originalIdx = filteredTransactions.indexOf(t);
        const dateKey = formatDateGroup(t.date_time);
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push({ txn: t, idx: originalIdx });
    });

    const dateKeys = Object.keys(grouped);

    const confirmTag = async (idx, category, subcategory) => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        const updated = await retagTransaction(idx, category, subcategory, true);
        const n = typeof updated === 'number' ? updated : 1;
        setStatus({
            active: true,
            text: `Remembered · ${n} payment${n === 1 ? '' : 's'} updated.`,
            type: 'success',
        });
        setExpandedIdx(null);
    };

    return (
        <div className="output-section">
            <div className="section-title txn-section-head">
                <div className="txn-section-titles">
                    <span className="txn-section-label">Transactions</span>
                </div>
                <span className="section-title-meta">{visible.length}</span>
            </div>

            {dateKeys.length === 0 ? (
                <p className="empty-hint txn-empty">
                    No transactions match these filters.
                </p>
            ) : (
                <div className="txn-list">
                    {dateKeys.map(dateKey => (
                        <React.Fragment key={dateKey}>
                            <div className="txn-date-header">
                                <span>{dateKey}</span>
                                <span className="txn-date-count">{grouped[dateKey].length}</span>
                            </div>
                            {grouped[dateKey].map(item => {
                                const t = item.txn;
                                const idx = item.idx;
                                const amountClass = t.type === 'CREDIT' ? 'credit' : 'debit';
                                const amountPrefix = t.type === 'CREDIT' ? '+' : '−';
                                const logoSrc = SOURCE_LOGOS[t.source];
                                const bankLogo = getBankLogo(t.account);
                                const timeOnly = formatTimeOnly(t.date_time);
                                const category = t.category || 'Uncategorized';
                                const isUncategorized = category === 'Uncategorized';
                                const catClass = getCategoryColorClass(category);
                                const isUserTagged = t.category_source === 'user' || t.category_source === 'manual';
                                const isGuess = isGuessTxn(t);
                                const peopleGuess = isAmbiguousPeopleGuess(t);
                                const isOpen = expandedIdx === idx;

                                return (
                                    <React.Fragment key={t.id || idx}>
                                        <div
                                            className={`txn-item ${isOpen ? 'open' : ''} ${isGuess ? 'txn-guess' : ''}`}
                                            onClick={() => setExpandedIdx(isOpen ? null : idx)}
                                        >
                                            {logoSrc && <img className="txn-logo" src={logoSrc} alt={t.source || ''} />}
                                            {bankLogo && <img className="txn-bank-logo" src={bankLogo} alt="" />}
                                            <div className="txn-info">
                                                <div className="txn-party">{t.party || 'Unknown'}</div>
                                                <div className="txn-meta">
                                                    <button
                                                        type="button"
                                                        className={`txn-category ${isUncategorized ? 'uncategorized' : catClass}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setRetagIdx(idx);
                                                        }}
                                                    >
                                                        {category}
                                                        {isUserTagged && <span className="txn-saved-badge">Saved</span>}
                                                        {isGuess && <span className="txn-guess-badge">Guess</span>}
                                                        {t.subcategory ? ` · ${t.subcategory}` : ''}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="txn-right">
                                                <div className={`txn-amount ${amountClass}`}>{amountPrefix}{t.amount}</div>
                                                <div className="txn-time">{timeOnly}</div>
                                            </div>
                                        </div>
                                        {isOpen && (
                                            <div className="txn-details show">
                                                <div className="txn-detail-row"><span>Type</span><span>{t.type}</span></div>
                                                {t.account && <div className="txn-detail-row"><span>Account</span><span>{t.account}</span></div>}
                                                <div className="txn-detail-row">
                                                    <span>Category</span>
                                                    <button
                                                        type="button"
                                                        className="txn-retag-link"
                                                        onClick={(e) => { e.stopPropagation(); setRetagIdx(idx); }}
                                                    >
                                                        Re-tag
                                                    </button>
                                                </div>
                                                {isUserTagged && (
                                                    <div className="txn-detail-row">
                                                        <span>Rule</span>
                                                        <span>Remembered on this device</span>
                                                    </div>
                                                )}
                                                {isGuess && (
                                                    <div className="txn-guess-panel" onClick={(e) => e.stopPropagation()}>
                                                        <p className="txn-guess-prompt">
                                                            {peopleGuess
                                                                ? 'Person or shop? (e.g. kirana on a personal UPI)'
                                                                : 'Not sure about this tag — confirm or fix.'}
                                                        </p>
                                                        <div className="txn-guess-actions">
                                                            {peopleGuess ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-pill"
                                                                        onClick={() => confirmTag(idx, 'Family & Friends', 'Personal Transfer')}
                                                                    >
                                                                        People
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-pill"
                                                                        onClick={() => confirmTag(idx, 'Shopping', 'General')}
                                                                    >
                                                                        Shop / Kirana
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="btn-pill"
                                                                    onClick={() => confirmTag(idx, t.category, t.subcategory || 'General')}
                                                                >
                                                                    Looks right
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn-pill"
                                                                onClick={() => setRetagIdx(idx)}
                                                            >
                                                                Fix…
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            )}

            {retagIdx !== null && (
                <RetagModal
                    txnIdx={retagIdx}
                    onClose={() => setRetagIdx(null)}
                />
            )}
        </div>
    );
}

export default TransactionList;
